import _ from 'underscore';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { AccountUtils, Constants, InsertionOrderUtils } from '@bingads-webui-clientcenter/entity-utils';
import { toHex, contrastColor } from '@bingads-webui-universal/primitive-utilities';
import { performanceMetricWithFluctuationTemplate, hasFluctuations, ColumnNameToFluctuationTypeEnum, formatMetricValue } from '@bingads-webui-campaign/campaignui-common-columns';
import { labelsColumnHeaderTemplate, labelsColumnTemplate } from '@bingads-webui-campaign/labels';

import {
  currentCid,
  column as legacyColumn,
  buildVNextColumn,
  isManagerAccount,
  isInPerformanceTab,
  isInImportTab,
  getSwitchCustomerShellLink,
  isAccountItemRow,
  getVNextCampaignWithCidAndAid,
  getVNextCampaignWithAid,
  getVNextAccountsSummaryWithCid,
} from '../common-utils';
import { EmptyColumnValue } from '../constants';
import { isInManagementTab } from '../helper/management-dashboard-helper';
import { importTabColumnsConfig } from './import-status-column-config';
import { managementTabColumnsConfig } from './management-column-config';
import { getSharedColumnConfiguration } from './shared-column-configuration';
import accountNameToCampaignTemplate from '../template/vnext/account-name-column.pug';
import accountModeColumnTemplate from '../template/vnext/account-mode-column.pug';
import accountTypeColumnTemplate from '../template/vnext/account-type-column.pug';
import alertIconColumnTemplate from '../template/vnext/alert-icon-column.pug';
import alertIconHeaderTemplate from '../template/vnext/alert-icon-header.pug';
import managerAccountColumnTemplate from '../template/vnext/manager-accounts-column.pug';
import statusColumnReadonlyTemplate from '../template/vnext/status-column-readonly.pug';
import textOverflowEllipsisTemplate from '../template/vnext/text-overflow-ellipsis.pug';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const urls = globalContext.require('campaignui/feature/app/urls');
const globalConfig = globalContext.require('component/config/index');
const logger = globalContext.require('module/service/instrumentation/index');
const qualityScoreTemplate = globalContext.require('campaignui/feature/common-grid-config/column/quality-score/quality-score-template');
const {
  formatUnlimitedIOBudgetInGrid,
  isEndlessIOEndDate,
  getSpendCapAmountNumber,
} = InsertionOrderUtils;

const {
  deletedReasonValueMatcher: isDeletedMatcher,
  isDeleted: isAccountDeleted,
} = AccountUtils;
const {
  IsCustomerHierarchyEnabled,
  IsManagementDashboardEnabled,
  IsAccountDashboardAlertsColumnAllowed,
  IsFixDeletedAccountLinksEnabled,
  IsAvgPositionDeprecationPhase2Enabled,
  IsSmartUIEnabled,
  IsInternalUser,
  IsAudienceSOVEnabled,
  IsAccountLabelsEnabled,
  IsViewThroughConversionsRevenueEnabled,
  IsUnifiedSmartMode,
  IsSmartMode,
  dynamic = {},
} = globalConfig.get('Permissions');

const isUnlimitedIOBudget = spendCapAmountValue => InsertionOrderUtils.isUnlimitedIOBudget((getSpendCapAmountNumber({ spendCapAmountValue, i18n })));

const columnConversion = (sourceMetricName, targetMetricName) => ({
  sourceColumnName: sourceMetricName,
  sourceSortableProperty: `PerformanceMetrics/${sourceMetricName}`,
  targetSortableProperty: `PerformanceMetrics/${targetMetricName}`,
});

export const columnConfiguration = ({
  gridType, isForVNext, hasUnifiedSmartAccount = false, i18n: newI18n,
}) => {
  // for vnext, $/i18n!global does not contain resources for accounts summary
  // we need to pass i18n for cloumn object creation
  const column = isForVNext ? buildVNextColumn(i18n) : legacyColumn;
  const { columnNames } = getSharedColumnConfiguration(globalConfig);
  const isPerformanceGrid = isInPerformanceTab(gridType);
  const {
    PartialConversions: IsPartialConversionsEnabled,
    ColumnConversion: IsColumnConversionEnabled,
    IsSmartPageStandalone: IsSmartPageStandaloneEnabled,
    NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
    CBTLifetimeBudget: IsCampaignLifetimeBudgetEnabled,
    ConvValueRules,
    CNMarketExpansion,
    BaiduFileImport,
  } = dynamic;
  const usePartialConversions = isPerformanceGrid && IsPartialConversionsEnabled && IsColumnConversionEnabled;
  const isConversionValueRuleColumnsEnabled = isPerformanceGrid && ConvValueRules;

  const statusColumn = (IsAccountDashboardAlertsColumnAllowed || isForVNext) ? column({
    name: 'AccountInactiveReasons',
    property: {
      get({ Id, AccountInactiveReasons }) {
        let accountInactiveReasons;

        if (IsFixDeletedAccountLinksEnabled) {
          accountInactiveReasons = _.any(AccountInactiveReasons, isDeletedMatcher) ? [] : AccountInactiveReasons;
        } else {
          accountInactiveReasons = _.reject(AccountInactiveReasons, isDeletedMatcher);
        }

        return {
          Id,
          AccountInactiveReasons: accountInactiveReasons,
          AccountActivityStatusEnum: Constants.AccountActivityStatus,
          activityStatus: AccountUtils.activityStatus({ AccountInactiveReasons }),
          isForVNext,
        };
      },
      key: ['Id', 'AccountInactiveReasons', 'AccountInactiveReasonsCount', 'BillToCustomerId'],
    },
    sortable: 'AccountInactiveReasonsCount',
    title: 'AccountGrid_Alerts',
    template: alertIconColumnTemplate,
    type: 'string',
    helpId: isForVNext ? undefined : 'Pop_BA_AccountsGrid_Alerts',
    bodyClasses: ['account-inactive-reasons-cell'],
    html: isForVNext ? alertIconHeaderTemplate({ helpId: 'Pop_BA_AccountsGrid_Alerts', helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: i18n.getString('AccountGrid_Alerts') }) }) : undefined,
  }) : column({
    name: 'Status',
    title: 'ColumnName_Status',
    template: statusColumnReadonlyTemplate,
    sortable: true,
    type: 'string',
    helpId: 'Pop_BA_Accounts_Index_Status',
  });

  const accountNameColumn = column({
    name: 'AccountName',
    title: 'ColumnName_AccountName',
    property: {
      get(account) {
        let link = isForVNext ? getVNextCampaignWithAid(account.Id) : urls.getCampaignWithAid(account.Id);
        // cid and aid are used for VNext client side navigation:
        let cid = currentCid();
        let aid = account.Id;

        if (IsCustomerHierarchyEnabled) {
          if (isManagerAccount(account)) {
            // account here means the data for the cell/row, if it is a manager account, then its Id is customer id
            aid = 0;
            cid = account.Id;
            link = isForVNext ? getVNextAccountsSummaryWithCid(cid) : urls.getAccountsSummaryWithCid(cid);
          } else {
            // if link is null, we don't have the <a> element
            cid = account.IsParentAuthorized ? account.OwnerId : currentCid();
            aid = account.Id;
            if (account.IsAccountAuthorized) {
              link = isForVNext ? getVNextCampaignWithCidAndAid(cid, aid) : urls.getCampaignWithCidAndAid(cid, aid);
            } else {
              link = null;
            }
          }
        }

        return {
          AccountName: account.Name,
          AccountNumber: account.AccountNumber,
          link,
          AggregateName: account.$total && account.$total.text,
          hasVNextDropdown: isForVNext && isInManagementTab(gridType),
          inactiveLabel: isForVNext && isAccountDeleted(account) && i18n.getString('AccountInfo_InActiveNote'),
          isForVNext,
          aid,
          cid,
          ariaLabel: i18n.getString('Click to scope to account {{accountName}}', { accountName: account.Name }),
        };
      },
      // ManagerAccounts is used for /cc/managers/bulkunlinkaccountsconfirmation call for Unlink button in toolbar.
      // So fetching ManagerAccounts with account name since this column is locked.
      key: ['Name', 'AccountNumber', 'Id', 'ManagerAccounts'],
    },
    template: accountNameToCampaignTemplate,
    sortable: 'Name',
    type: 'string',
  });

  if (isInImportTab(gridType)) {
    // pilot logs
    logger.logMessage([logger.constants.LOGCATEGORY.TRACE], logger.constants.LOGLEVEL.INFO, '/accounts/import', `CNMarketExpansion: ${CNMarketExpansion}`);
    logger.logMessage([logger.constants.LOGCATEGORY.TRACE], logger.constants.LOGLEVEL.INFO, '/accounts/import', `BaiduFileImport: ${BaiduFileImport}`);

    return _.union([statusColumn, accountNameColumn], importTabColumnsConfig({ isForVNext }));
  }

  let accountsSummaryColumns = _.compact([
    statusColumn,
    accountNameColumn,
    (IsSmartUIEnabled || hasUnifiedSmartAccount) && column({
      name: 'AccountMode',
      title: 'ColumnName_AccountMode',
      property: {
        get(account) {
          let mode;
          if (!isManagerAccount(account) && isAccountItemRow(account)) {
            mode = account.AccountMode ? i18n.getString(`AccountMode_${account.AccountMode}`) : i18n.getString('AccountMode_0');
          } else {
            // for accounts created before being in pilot, `AccountMode` will be `null`
            // Also grid entries for linked Manager Accounts will be null
            mode = EmptyColumnValue;
          }
          return {
            AccountMode: mode,
          };
        },
        key: ['AccountMode'],
      },
      template: accountModeColumnTemplate,
      sortable: 'AccountMode',
      type: 'string',
    }),
    column({
      name: columnNames.Currency,
      title: 'ColumnName_AccountDashboard_Currency',
      sortable: true,
      type: 'string',
    }),
    column({
      name: 'Clicks',
      title: 'ColumnName_Clicks',
      property: {
        get({ PerformanceMetrics, AvailableInsightTypeIds }) {
          return {
            metricType: ColumnNameToFluctuationTypeEnum.Clicks,
            metricName: i18n.getString('ColumnName_Clicks'),
            metricValue: formatMetricValue({ i18n, format: 'integer', metricValue: _.result(PerformanceMetrics, 'Clicks') }),
            hasFluctuation: hasFluctuations({ propertyName: 'Clicks', availableInsightTypeIds: AvailableInsightTypeIds }),
          };
        },
        key: ['PerformanceMetrics/Clicks', 'AvailableInsightTypeIds'],
      },
      sortable: 'PerformanceMetrics/Clicks',
      type: 'number',
      format: 'integer',
      helpId: 'Pop_BA_AccountsGrid_Clicks',
      template: performanceMetricWithFluctuationTemplate,
    }),
    column({
      name: 'Impressions',
      title: 'ColumnName_Impressions',
      property: {
        get({ PerformanceMetrics, AvailableInsightTypeIds }) {
          return {
            metricType: ColumnNameToFluctuationTypeEnum.Impressions,
            metricName: i18n.getString('ColumnName_Impressions'),
            metricValue: formatMetricValue({ i18n, format: 'integer', metricValue: _.result(PerformanceMetrics, 'Impressions') }),
            hasFluctuation: hasFluctuations({ propertyName: 'Impressions', availableInsightTypeIds: AvailableInsightTypeIds }),
          };
        },
        key: ['PerformanceMetrics/Impressions', 'AvailableInsightTypeIds'],
      },
      sortable: 'PerformanceMetrics/Impressions',
      type: 'number',
      format: 'integer',
      helpId: 'Pop_BA_AccountsGrid_Impr',
      template: performanceMetricWithFluctuationTemplate,
    }),
    IsAccountLabelsEnabled && column({
      name: 'Labels',
      title: 'ColumnName_Labels',
      property: {
        get(account) {
          const { Labels } = account;
          if (Labels) {
            const maxLabelsToShow = 2;
            const labelsLength = _.isEmpty(Labels) ? 0 : Labels.length;
            const labelsCountToShow = _.min([labelsLength, maxLabelsToShow]);
            const labelsToUse = [];

            for (let i = 0; i < labelsCountToShow; i += 1) {
              const label = Labels[i];
              const title = _.isEmpty(label.Description) ?
                label.Name :
                i18n.getString('label_tooltip_format').replace('{0}', label.Name).replace('{1}', label.Description);

              labelsToUse.push(_.extend(
                _.pick(label, 'Id', 'Name', 'Scope'),
                {
                  labelColor: toHex(label.ColorCode),
                  contractColor: toHex(contrastColor(label.ColorCode)),
                  title,
                }
              ));
            }
            return {
              Labels: labelsToUse,
              ShowViewMoreLink: labelsLength > maxLabelsToShow,
              TotalLength: labelsLength,
              IsAggregateRow: !!account.$total,
              i18n,
              isMCCLabelEnabled: IsAccountLabelsEnabled,
            };
          }
          return {
            IsAggregateRow: !!account.$total,
            Labels: [],
          };
        },
        key: ['Labels'],
      },
      type: 'string',
      format: 'integer',
      helpId: 'pop_BA_CampaignGrids_Labels',
      template: args => labelsColumnTemplate(args.value),
      headerTemplate: args => labelsColumnHeaderTemplate({ i18n, ...args }),
    }),
    column({
      name: 'CTR',
      title: 'ColumnName_ClickThruRate',
      property: 'PerformanceMetrics/CTR',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'Pop_BA_AccountsGrid_CTR',
    }),
    usePartialConversions ?
      column({
        name: 'ConversionsCredit',
        title: 'ColumnName_Conversions',
        property: {
          get({ PerformanceMetrics, AvailableInsightTypeIds }) {
            const metricValue = formatMetricValue({ i18n, format: 'decimal', metricValue: _.result(PerformanceMetrics, 'ConversionsCredit') });
            const obj = {
              metricType: ColumnNameToFluctuationTypeEnum.Conversions,
              metricName: i18n.getString('ColumnName_Conversions'),
              metricValue,
              hasFluctuation: hasFluctuations({ propertyName: 'ConversionsCredit', availableInsightTypeIds: AvailableInsightTypeIds }),
            };
            return obj;
          },
          key: ['PerformanceMetrics/ConversionsCredit', 'AvailableInsightTypeIds'],
        },
        sortable: 'PerformanceMetrics/ConversionsCredit',
        type: 'number',
        format: 'decimal',
        helpId: 'Pop_BA_AccountsGrid_Conv',
        template: performanceMetricWithFluctuationTemplate,
        columnConversion: columnConversion('Conversions', 'ConversionsCredit'),
      }) :
      column({
        name: 'Conversions',
        title: 'ColumnName_Conversions',
        property: {
          get({ PerformanceMetrics, AvailableInsightTypeIds }) {
            const metricValue = formatMetricValue({ i18n, format: 'integer', metricValue: _.result(PerformanceMetrics, 'Conversions') });
            const obj = {
              metricType: ColumnNameToFluctuationTypeEnum.Conversions,
              metricName: i18n.getString('ColumnName_Conversions'),
              metricValue,
              hasFluctuation: hasFluctuations({ propertyName: 'Conversions', availableInsightTypeIds: AvailableInsightTypeIds }),
            };
            return obj;
          },
          key: ['PerformanceMetrics/Conversions', 'AvailableInsightTypeIds'],
        },
        sortable: 'PerformanceMetrics/Conversions',
        type: 'number',
        format: 'integer',
        helpId: 'Pop_BA_AccountsGrid_Conv',
        template: performanceMetricWithFluctuationTemplate,
        columnConversion: columnConversion('ConversionsCredit', 'Conversions'),
      }),
    column({
      name: 'AverageCPC',
      title: 'ColumnName_AverageCPC',
      property: 'PerformanceMetrics/AverageCPC',
      sortable: true,
      type: 'number',
      helpId: 'Pop_BA_AccountsGrid_CPC',
    }),
    column({
      name: 'AverageCPM',
      title: 'ColumnName_CostPerImpression',
      property: 'PerformanceMetrics/AverageCPM',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_AvgCPM',
    }),
    column({
      name: 'VideoViews',
      title: 'ColumnName_VideoViews',
      property: 'PerformanceMetrics/VideoViews',
      sortable: true,
      type: 'number',
      format: 'integer',
      helpId: 'Page_CampaignGrids_VideoViews',
    }),
    column({
      name: 'ViewThroughRate',
      title: 'ColumnName_ViewThroughRate',
      property: 'PerformanceMetrics/ViewThroughRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'Page_CampaignGrids_ViewThroughRate',
    }),
    column({
      name: 'AverageCPV',
      title: 'ColumnName_AverageCPV',
      property: 'PerformanceMetrics/AverageCPV',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_AverageCPV',
    }),
    column({
      name: 'TotalWatchTimeInMS',
      title: 'ColumnName_TotalWatchTimeInMS',
      property: 'PerformanceMetrics/TotalWatchTimeInMS',
      sortable: true,
      format: 'thousandth',
      type: 'number',
      helpId: 'Page_CampaignGrids_TotalWatchTime',
    }),
    column({
      name: 'AverageWatchTimePerImpression',
      title: 'ColumnName_AverageWatchTimePerImpression',
      property: 'PerformanceMetrics/AverageWatchTimePerImpression',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_AverageWatchTimePerImpression',
    }),
    column({
      name: 'AverageWatchTimePerVideoView',
      title: 'ColumnName_AverageWatchTimePerVideoView',
      property: 'PerformanceMetrics/AverageWatchTimePerVideoView',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_AverageWatchTimePerVideoView',
    }),
    column({
      name: 'VideoViewsAt25Percent',
      title: 'ColumnName_VideoViewsAt25Percent',
      property: 'PerformanceMetrics/VideoViewsAt25Percent',
      sortable: true,
      type: 'number',
      format: 'integer',
      helpId: 'Page_CampaignGrids_VideoViewsAt25Percent',
    }),
    column({
      name: 'VideoViewsAt50Percent',
      title: 'ColumnName_VideoViewsAt50Percent',
      property: 'PerformanceMetrics/VideoViewsAt50Percent',
      sortable: true,
      type: 'number',
      format: 'integer',
      helpId: 'Page_CampaignGrids_VideoViewsAt50Percent',
    }),
    column({
      name: 'VideoViewsAt75Percent',
      title: 'ColumnName_VideoViewsAt75Percent',
      property: 'PerformanceMetrics/VideoViewsAt75Percent',
      sortable: true,
      type: 'number',
      format: 'integer',
      helpId: 'Page_CampaignGrids_VideoViewsAt75Percent',
    }),
    column({
      name: 'CompletedVideoViews',
      title: 'ColumnName_CompletedVideoViews',
      property: 'PerformanceMetrics/CompletedVideoViews',
      sortable: true,
      type: 'number',
      format: 'integer',
      helpId: 'Page_CampaignGrids_CompletedVideoViews',
    }),
    column({
      name: 'VideoCompletionRate',
      title: 'ColumnName_VideoCompletionRate',
      property: 'PerformanceMetrics/VideoCompletionRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'Page_CampaignGrids_VideoCompletionRate',
    }),
    column({
      name: 'AveragePosition',
      title: 'FilterLocalization_DataAttribute_AveragePosition',
      property: 'PerformanceMetrics/AveragePosition',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_AvgPosition',
    }),
    usePartialConversions ?
      column({
        name: 'PartialConversionCPA',
        title: 'FilterLocalization_DataAttribute_CPA',
        property: 'PerformanceMetrics/PartialConversionCPA',
        sortable: true,
        type: 'number',
        helpId: 'Page_CampaignGrids_CPA',
        columnConversion: columnConversion('CPA', 'PartialConversionCPA'),
      }) :
      column({
        name: 'CPA',
        title: 'FilterLocalization_DataAttribute_CPA',
        property: 'PerformanceMetrics/CPA',
        sortable: true,
        type: 'number',
        helpId: 'Page_CampaignGrids_CPA',
        columnConversion: columnConversion('PartialConversionCPA', 'CPA'),
      }),
    usePartialConversions ?
      column({
        name: 'PartialConversionRate',
        title: 'FilterLocalization_DataAttribute_ConversionRate',
        property: 'PerformanceMetrics/PartialConversionRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'Page_CampaignGrids_ConvRate',
        columnConversion: columnConversion('ConversionRate', 'PartialConversionRate'),
      }) :
      column({
        name: 'ConversionRate',
        title: 'FilterLocalization_DataAttribute_ConversionRate',
        property: 'PerformanceMetrics/ConversionRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'Page_CampaignGrids_ConvRate',
        columnConversion: columnConversion('PartialConversionRate', 'ConversionRate'),
      }),
    column({
      name: 'AdvertiserReportedRevenue',
      title: 'FilterLocalization_DataAttribute_AdvertiserReportedRevenue',
      property: 'PerformanceMetrics/AdvertiserReportedRevenue',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_Revenue',
    }),
    column({
      name: 'RevenueOnAdSpend',
      title: 'ColumnName_RevenueOnAdSpend',
      property: 'PerformanceMetrics/RevenueOnAdSpend',
      sortable: true,
      type: 'number',
      helpId: 'Page_CampaignGrids_ReturnAdSpend',
    }),
    usePartialConversions ?
      column({
        name: 'AllConversionsCredit',
        title: 'ColumnName_AllConversions',
        property: 'PerformanceMetrics/AllConversionsCredit',
        sortable: true,
        type: 'number',
        format: 'decimal',
        helpId: 'pop_BA_CampaignGrids_AllConv',
        columnConversion: columnConversion('AllConversions', 'AllConversionsCredit'),
      }) :
      column({
        name: 'AllConversions',
        title: 'ColumnName_AllConversions',
        property: 'PerformanceMetrics/AllConversions',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'pop_BA_CampaignGrids_AllConv',
        columnConversion: columnConversion('AllConversionsCredit', 'AllConversions'),
      }),
    usePartialConversions ?
      column({
        name: 'AllPartialConversionCPA',
        title: 'ColumnName_AllConversionCPA',
        property: 'PerformanceMetrics/AllPartialConversionCPA',
        sortable: true,
        type: 'number',
        helpId: 'pop_BA_CampaignGrids_AllConvCPA',
        columnConversion: columnConversion('AllConversionCPA', 'AllPartialConversionCPA'),
      }) :
      column({
        name: 'AllConversionCPA',
        title: 'ColumnName_AllConversionCPA',
        property: 'PerformanceMetrics/AllConversionCPA',
        sortable: true,
        type: 'number',
        helpId: 'pop_BA_CampaignGrids_AllConvCPA',
        columnConversion: columnConversion('AllPartialConversionCPA', 'AllConversionCPA'),
      }),
    usePartialConversions ?
      column({
        name: 'AllPartialConversionRate',
        title: 'ColumnName_AllConversionRate',
        property: 'PerformanceMetrics/AllPartialConversionRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'pop_BA_CampaignGrids_AllConvRate',
        columnConversion: columnConversion('AllConversionRate', 'AllPartialConversionRate'),
      }) :
      column({
        name: 'AllConversionRate',
        title: 'ColumnName_AllConversionRate',
        property: 'PerformanceMetrics/AllConversionRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'pop_BA_CampaignGrids_AllConvRate',
        columnConversion: columnConversion('AllPartialConversionRate', 'AllConversionRate'),
      }),
    column({
      name: 'AllConversionAdvertiserReportedRevenue',
      title: 'ColumnName_AllConversionAdvertiserReportedRevenue',
      property: 'PerformanceMetrics/AllConversionAdvertiserReportedRevenue',
      sortable: true,
      type: 'number',
      helpId: 'pop_BA_CampaignGrids_AllConvRevenue',
    }),
    column({
      name: 'AllConversionRevenueOnAdSpend',
      title: 'ColumnName_AllConversionRevenueOnAdSpend',
      property: 'PerformanceMetrics/AllConversionRevenueOnAdSpend',
      sortable: true,
      type: 'number',
      helpId: 'pop_BA_CampaignGrids_AllConvROAS',
    }),
    usePartialConversions ?
      column({
        name: 'ViewThroughConversionsCredit',
        title: 'ColumnName_ViewThroughConversions',
        property: 'PerformanceMetrics/ViewThroughConversionsCredit',
        sortable: true,
        type: 'number',
        format: 'decimal',
        helpId: 'pop_BA_CampaignGrids_ViewThrough',
        columnConversion: columnConversion('ViewThroughConversions', 'ViewThroughConversionsCredit'),
      }) :
      column({
        name: 'ViewThroughConversions',
        title: 'ColumnName_ViewThroughConversions',
        property: 'PerformanceMetrics/ViewThroughConversions',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'pop_BA_CampaignGrids_ViewThrough',
        columnConversion: columnConversion('ViewThroughConversionsCredit', 'ViewThroughConversions'),
      }),
    column({
      name: 'TotalEffectiveCost',
      title: 'ColumnName_TotalEffectiveCost',
      property: {
        get(account) {
          const { PerformanceMetrics, AvailableInsightTypeIds, Id } = account;
          return {
            metricType: ColumnNameToFluctuationTypeEnum.Spend,
            metricName: i18n.getString('ColumnName_TotalEffectiveCost'),
            metricValue: formatMetricValue({ i18n, format: 'decimal', metricValue: _.result(PerformanceMetrics, 'Spend') }),
            hasFluctuation: hasFluctuations({ propertyName: 'Spend', availableInsightTypeIds: AvailableInsightTypeIds }),
            isBudgetCenterEntryEnabled: Id && !IsSmartPageStandaloneEnabled && !IsSmartMode && !IsUnifiedSmartMode, // Only show budget center entry for row with accountId
          };
        },
        key: ['PerformanceMetrics/Spend', 'AvailableInsightTypeIds'],
      },
      sortable: 'PerformanceMetrics/Spend',
      type: 'number',
      helpId: 'Pop_BA_AccountsGrid_Spend',
      template: performanceMetricWithFluctuationTemplate,
    }),
    column({
      name: 'CompanyName',
      title: 'ColumnName_CompanyName',
      sortable: true,
      type: 'string',
      helpId: 'Pop_BA_AccountsGrid_CoName',
    }),
    column({
      name: 'BillToCustomerName',
      title: 'ColumnName_BillToCustomerName',
      sortable: true,
      type: 'string',
      helpId: 'Pop_BA_AccountsGrid_BillTo',
      bodyClasses: ['bill-to-customer-cell'],
      template: isForVNext ? textOverflowEllipsisTemplate : undefined,
    }),
    column({
      name: 'IOPurchaseOrder',
      title: 'ColumnName_IOPurchaseOrder',
      property: 'InsertionOrderDetails/CurrentInsertionOrder/PurchaseOrder',
      sortable: true,
      type: 'string',
      helpId: 'Pop_BA_AccountsGrid_PO',
    }),
    column({
      name: 'IOBudget',
      title: 'ColumnName_IOBudget',
      property: {
        get({ InsertionOrderDetails }) {
          const { CurrentInsertionOrder: { Budget } = {} } = InsertionOrderDetails || {};
          return formatUnlimitedIOBudgetInGrid({ i18n, spendCapAmountValue: Budget });
        },
        key: ['InsertionOrderDetails/CurrentInsertionOrder/Budget'],
      },
      sortable: 'InsertionOrderDetails/CurrentInsertionOrder/Budget',
      type: 'string',
      helpId: 'Pop_BA_AccountsGrid_Budget',
    }),
    column({
      name: 'RemainingIOBudget',
      title: 'ColumnName_RemainingIOBudget',
      property: {
        get({ InsertionOrderDetails }) {
          const { CurrentInsertionOrder: { Budget, RemainingBudget } = {} } = InsertionOrderDetails || {};
          return isUnlimitedIOBudget(Budget) ? null : RemainingBudget;
        },
        key: ['InsertionOrderDetails/CurrentInsertionOrder/RemainingBudget'],
      },
      sortable: 'InsertionOrderDetails/CurrentInsertionOrder/RemainingBudget',
      type: 'number',
      helpId: 'Pop_BA_AccountsGrid_BudgetRem',
    }),
    column({
      name: 'StartDate',
      title: 'ColumnName_StartDate',
      property: 'InsertionOrderDetails/CurrentInsertionOrder/StartDate',
      sortable: true,
      type: 'date',
      helpId: 'Pop_BA_AccountsGrid_StartDate',
    }),
    column({
      name: 'EndDate',
      title: 'ColumnName_EndDate',
      property: {
        get({ InsertionOrderDetails }) {
          const { CurrentInsertionOrder: { EndDate } = {} } = InsertionOrderDetails || {};

          if (isEndlessIOEndDate(_.isString(EndDate) && new Date(EndDate.split('T')[0]))) {
            return i18n.getString('InsertionOrder_NoEndDate');
          }
          return EndDate;
        },
        key: ['InsertionOrderDetails/CurrentInsertionOrder/EndDate'],
      },
      sortable: 'InsertionOrderDetails/CurrentInsertionOrder/EndDate',
      type: 'date',
      helpId: 'Pop_BA_AccountsGrid_EndDate',
    }),
    column({
      name: columnNames.DailyBudget,
      title: 'ColumnName_DailyBudget',
      property: 'Budget/DailyBudget',
      sortable: true,
      type: 'number',
      helpId: 'Pop_BA_AccountsGrid_TotDailyBudget',
    }),
    IsCampaignLifetimeBudgetEnabled && column({
      name: columnNames.CampaignLifetimeBudget,
      title: 'ColumnName_CampaignLifetimeBudget',
      property: 'Budget/LifetimeBudget',
      sortable: true,
      type: 'number',
      helpId: 'pop_BA_CampaignsGrids_TotalCampBudget',
    }),
    column({
      name: 'QualityScore',
      title: 'FilterLocalization_DataAttribute_QualityScore',
      property: {
        get(account) {
          return _.defaults({ i18n }, _.pick(account.QualityScoreData, 'OverallQualityScore'));
        },
        key: 'QualityScoreData/OverallQualityScore',
      },
      template: qualityScoreTemplate,
      sortable: true,
      helpId: 'Page_CampaignGrids_QualScore',
      bodyClasses: ['quality-score-cell'],
    }),
    column({
      name: 'TotalRemainingIOBudget',
      title: 'ColumnName_TotalRemainingIOBudget',
      property: {
        get({ InsertionOrderDetails }) {
          const { TotalRemainingBudget } = InsertionOrderDetails || {};
          return isUnlimitedIOBudget(TotalRemainingBudget) ? null : TotalRemainingBudget;
        },
        key: 'InsertionOrderDetails/TotalRemainingBudget',
      },
      sortable: true,
      type: 'string',
      helpId: 'Pop_BA_AccountsGrid_TotBudget',
    }),
    column({
      name: 'OverallEndDate',
      title: 'ColumnName_OverallEndDate',
      property: 'InsertionOrderDetails/OverallEndDate',
      sortable: true,
      type: 'date',
      helpId: 'Pop_BA_AccountsGrid_LastEndDate',
    }),
    column({
      name: 'ImpShare',
      title: 'ColumnName_ImpShare',
      property: 'PerformanceMetrics/AuctionWonPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_CampaignGrids_IS',
    }),
    column({
      name: 'ImpShareLostToBudget',
      title: 'ColumnName_ImpShareLostToBudget',
      property: 'PerformanceMetrics/AuctionLostToBudgetPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_CampaignGrids_ISBudget',
    }),
    column({
      name: 'ImpShareLostToRank',
      title: 'ColumnName_ImpShareLostToRank',
      property: 'PerformanceMetrics/AuctionLostToRankPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_CampaignGrids_ISRank',
    }),
    column({
      name: 'ClickSharePercent',
      title: 'ColumnName_ClickSharePercent',
      property: 'PerformanceMetrics/ClickSharePercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_ProductAds_ClickSharePercent',
    }),
    column({
      name: 'AbsoluteTopImpressionSharePercent',
      title: 'ColumnName_AbsoluteTopImpressionSharePercent',
      property: 'PerformanceMetrics/AbsoluteTopImpressionSharePercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_ProductAds_AbsoluteTopImpShare',
    }),
    IsAudienceSOVEnabled && column({
      name: 'AudienceImpressionShare',
      title: 'ColumnName_AudienceImpressionShare',
      property: 'PerformanceMetrics/AudienceAuctionWonPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AudienceNetwork_ImpressionShare',
    }),
    IsAudienceSOVEnabled && column({
      name: 'AudienceImpressionShareLostToBudget',
      title: 'ColumnName_AudienceImpressionShareLostToBudget',
      property: 'PerformanceMetrics/AudienceTopISLostToBudgetPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AudienceNetwork_ImpressionShare_LostBudget',
    }),
    IsAudienceSOVEnabled && column({
      name: 'AudienceImpressionShareLostToRank',
      title: 'ColumnName_AudienceImpressionShareLostToRank',
      property: 'PerformanceMetrics/AudienceTopISLostToRankPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AudienceNetwork_ImpressionShare_LostRank',
    }),
    IsViewThroughConversionsRevenueEnabled && column({
      name: 'ViewThroughConversionsRevenue',
      title: 'ColumnName_ViewThroughConversionsRevenue',
      property: 'PerformanceMetrics/ViewThroughConversionsRevenue',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvRevenue',
    }),
    IsViewThroughConversionsRevenueEnabled && usePartialConversions && column({
      name: 'ViewThroughPartialConversionsCPA',
      title: 'ColumnName_ViewThroughConversionsCPA',
      property: 'PerformanceMetrics/ViewThroughPartialConversionsCPA',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
      columnConversion: columnConversion('ViewThroughConversionsCPA', 'ViewThroughPartialConversionsCPA'),
    }),
    IsViewThroughConversionsRevenueEnabled && !usePartialConversions && column({
      name: 'ViewThroughConversionsCPA',
      title: 'ColumnName_ViewThroughConversionsCPA',
      property: 'PerformanceMetrics/ViewThroughConversionsCPA',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
      columnConversion: columnConversion('ViewThroughPartialConversionsCPA', 'ViewThroughConversionsCPA'),
    }),
    IsViewThroughConversionsRevenueEnabled && column({
      name: 'ViewThroughConversionsReturnOnAdSpend',
      title: 'ColumnName_ViewThroughConversionsReturnOnAdSpend',
      property: 'PerformanceMetrics/ViewThroughConversionsReturnOnAdSpend',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvReturnOnAdSpend',
    }),
    IsViewThroughConversionsRevenueEnabled && usePartialConversions && column({
      name: 'ViewThroughPartialConversionsRate',
      title: 'ColumnName_ViewThroughConversionsRate',
      property: 'PerformanceMetrics/ViewThroughPartialConversionsRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
      columnConversion: columnConversion('ViewThroughConversionsRate', 'ViewThroughPartialConversionsRate'),
    }),
    IsViewThroughConversionsRevenueEnabled && !usePartialConversions && column({
      name: 'ViewThroughConversionsRate',
      title: 'ColumnName_ViewThroughConversionsRate',
      property: 'PerformanceMetrics/ViewThroughConversionsRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
      columnConversion: columnConversion('ViewThroughPartialConversionsRate', 'ViewThroughConversionsRate'),
    }),
    isConversionValueRuleColumnsEnabled && column({
      name: 'AdvertiserReportedRevenueAdjustment',
      title: 'ColumnName_AdvertiserReportedRevenueAdjustment',
      property: 'PerformanceMetrics/AdvertiserReportedRevenueAdjustment',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_CampaignGrids_RevenueAdjustment',
    }),
    isConversionValueRuleColumnsEnabled && column({
      name: 'ViewThroughConversionsRevenueAdjustment',
      title: 'ColumnName_ViewThroughConversionsRevenueAdjustment',
      property: 'PerformanceMetrics/ViewThroughConversionsRevenueAdjustment',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_CampaignGrids_ViewthroughRevenueAdjustment',
    }),
    isConversionValueRuleColumnsEnabled && column({
      name: 'AllConversionAdvertiserReportedRevenueAdjustment',
      title: 'ColumnName_AllConversionAdvertiserReportedRevenueAdjustment',
      property: 'PerformanceMetrics/AllConversionAdvertiserReportedRevenueAdjustment',
      sortable: true,
      type: 'number',
      format: 'decimal',
      helpId: 'pop_BA_CampaignGrids_AllRevenueAdjustment',
    }),
  ]);

  if (IsCustomerHierarchyEnabled || IsManagementDashboardEnabled) {
    accountsSummaryColumns = _.without(accountsSummaryColumns, _.findWhere(accountsSummaryColumns, { name: 'CompanyName' }));

    accountsSummaryColumns.push(column({
      name: 'Owner',
      title: 'ColumnName_Owner',
      sortable: true,
      type: 'string',
      helpId: 'pop_BA_Accounts_MD_Owner',
      bodyClasses: _.compact(['owners-accounts-cell']),
      template: isForVNext ? textOverflowEllipsisTemplate : undefined,
    }));
  }

  if (IsCustomerHierarchyEnabled) {
    accountsSummaryColumns.push(
      column({
        name: 'AccountType',
        title: 'ColumnName_AccountType',
        sortable: 'AccountType',
        type: 'string',
        template: accountTypeColumnTemplate,
        property: {
          get({ AccountType }) {
            return {
              AccountType: i18n.getString(`AccountType_${AccountType}`),
            };
          },
          key: ['AccountType'],
        },
        helpId: 'pop_BA_Accounts_MD_AccountType',
      }),
      column({
        name: 'ManagerAccounts',
        title: isForVNext ? 'ColumnName_DirectManagers' : 'ColumnName_ManagerAccounts',
        sortable: false,
        type: 'string',
        template: managerAccountColumnTemplate,
        property: {
          get({ Id, ManagerAccounts }) {
            return {
              Id,
              ManagerAccounts,
              isForVNext,
              // for multiple manager accounts, we'll add links in the popover content
              singleAccountLink: (IsInternalUser && _.size(ManagerAccounts) === 1)
                ? getSwitchCustomerShellLink((ManagerAccounts[0] || {}).Id) : '',
            };
          },
          key: ['ManagerAccounts'],
        },
        helpId: 'pop_BA_Accounts_MD_DirectManager',
        bodyClasses: ['manager-accounts-cell'],
      })
    );
  }

  if (isInManagementTab(gridType)) {
    accountsSummaryColumns = accountsSummaryColumns.concat(managementTabColumnsConfig({
      column, isForVNext, i18n: newI18n,
    }));
  }

  if (IsAvgPositionDeprecationPhase2Enabled) {
    accountsSummaryColumns = _.reject(accountsSummaryColumns, ({ name }) => name === 'AveragePosition');
  }

  accountsSummaryColumns = accountsSummaryColumns.concat([
    column({
      name: 'TopImpressionRate',
      title: 'ColumnName_TopImpressionRate',
      property: 'PerformanceMetrics/TopImpressionRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_TopImpressionRate',
    }),
    column({
      name: 'AbsoluteTopImpressionRate',
      title: 'ColumnName_AbsoluteTopImpressionRate',
      property: 'PerformanceMetrics/AbsoluteTopImpressionRate',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AbsoluteTopImpressionRate',
    }),
    column({
      name: 'ExactMatchImpressionSharePercent',
      title: 'ColumnName_ExactMatchImpressionSharePercent',
      property: 'PerformanceMetrics/ExactMatchImpressionSharePercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_ExactMatchImpressionShare',
    }),
    column({
      name: 'TopImpressionSharePercent',
      title: 'ColumnName_TopImpressionSharePercent',
      property: 'PerformanceMetrics/TopImpressionSharePercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_TopImpressionShare',
    }),
    column({
      name: 'TopImpressionShareLostToBudget',
      title: 'ColumnName_TopImpressionShareLostToBudget',
      property: 'PerformanceMetrics/TopISLostToBudgetPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_TopImpressionLosttoBudget',
    }),
    column({
      name: 'TopImpressionShareLostToRank',
      title: 'ColumnName_TopImpressionShareLostToRank',
      property: 'PerformanceMetrics/TopISLostToRankPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_TopImpressionLosttoRank',
    }),
    column({
      name: 'AbsoluteTopImpressionShareLostToBudget',
      title: 'ColumnName_AbsoluteTopImpressionShareLostToBudget',
      property: 'PerformanceMetrics/AbsoluteTopISLostToBudgetPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AbsoluteTopISLosttoBudget',
    }),
    column({
      name: 'AbsoluteTopImpressionShareLostToRank',
      title: 'ColumnName_AbsoluteTopImpressionShareLostToRank',
      property: 'PerformanceMetrics/AbsoluteTopISLostToRankPercent',
      sortable: true,
      type: 'number',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AbsoluteTopISLosttoRank',
    }),
  ]);

  if (dynamic.CPSReporting) {
    accountsSummaryColumns = accountsSummaryColumns.concat([
      column({
        name: 'SalesCount',
        title: 'ColumnName_SalesCount',
        property: 'PerformanceMetrics/SalesCount',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'pop_BA_CampaignGrids_Sale',
      }),
    ]);
  }

  if (dynamic.InstallPerformanceColumns) {
    accountsSummaryColumns = accountsSummaryColumns.concat([
      column({
        name: 'Installs',
        title: 'ColumnName_Installs',
        property: 'PerformanceMetrics/Installs',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'pop_BA_StoreAds_Install',
      }),
    ]);
  }

  if (isNewCustomerAcquisitionEnabled) {
    accountsSummaryColumns = accountsSummaryColumns.concat([
      column({
        name: 'NewCustomerConversions',
        title: 'ColumnName_NewCustomerConversions',
        property: 'PerformanceMetrics/NewCustomerConversions',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'pop_BA_CampaignGrids_NewCustomerConversions',
      }),
      column({
        name: 'NewCustomerRevenue',
        title: 'ColumnName_NewCustomerRevenue',
        property: 'PerformanceMetrics/NewCustomerRevenue',
        sortable: true,
        type: 'number',
        format: 'decimal',
        helpId: 'pop_BA_CampaignGrids_NewCustomerConversionsValue',
      }),
      column({
        name: 'NewCustomerConversionRate',
        title: 'ColumnName_NewCustomerConversionRate',
        property: 'PerformanceMetrics/NewCustomerConversionRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'pop_BA_CampaignGrids_NewCustomerConversionsRate',
      }),
      column({
        name: 'NewCustomerCPA',
        title: 'ColumnName_NewCustomerCPA',
        property: 'PerformanceMetrics/NewCustomerCPA',
        sortable: true,
        type: 'number',
        format: 'decimal',
        helpId: 'pop_BA_CampaignGrids_NewCustomerCPA',
      }),
      column({
        name: 'NewCustomerRevenueOnAdSpend',
        title: 'ColumnName_NewCustomerRevenueOnAdSpend',
        property: 'PerformanceMetrics/NewCustomerRevenueOnAdSpend',
        sortable: true,
        type: 'percentage',
        helpId: 'pop_BA_CampaignGrids_NewCustomerRevenueOnAdSpend',
      }),
    ]);
  }


  return accountsSummaryColumns;
};
